/**
 * PGSS-5318 — wording for the staff → supplier note channel added by PGSS-5331/PGSS-5332.
 *
 * The channel itself is right: staff compose a note on Send to Supplier, the supplier reads it on Submit
 * Quote, and nothing is copied there implicitly (PGSS-5289). What failed QA is that both ends were worded
 * for one round only — the re-quote — while the screen is shown on *every* send, the first one included:
 *
 *   - Send to Supplier asked "Why are they being asked to quote again?" on an initial RFQ, where nobody has
 *     quoted yet and there is nothing to ask again.
 *   - Submit Quote filed the note under "Revision Note" on an initial RFQ, where no revision has happened.
 *
 * Two separate answers, because the two ends know different things.
 *
 * Staff know the round. By the time Send to Supplier opens, the dialog has both the suppliers already
 * contacted for this order and the Make it In Review hand-off for this round, and either one means this
 * ORDER has been out for quote before — so the prompt can name the round.
 *
 * Note what that does NOT mean. The picker renders already-contacted suppliers disabled and pre-ticked, and
 * the send payload carries `selectedSuppliers` only, so a note can only ever reach a supplier who has NOT
 * quoted this order. The round is a fact about the order; it is never a fact about the recipient. The
 * prompt has to be phrased against the order for that reason — asking staff why "they" are quoting "again"
 * describes someone who is not on the other end of this note.
 *
 * The supplier does not, and does not need to. Since PGSS-5332 the only text that reaches them is the note
 * staff wrote for that one send; the customer-side revision note stopped travelling in PGSS-5289. So the
 * heading names the channel rather than the round, and mirrors the label staff typed under, which is true on
 * round one and round five alike. If a round *is* a re-quote, the note staff wrote says so — that sentence
 * is the whole reason the field exists.
 */

/**
 * Has this order been in front of a supplier before? Both signals are already loaded by the Send to Supplier
 * dialog:
 *
 *   previousSuppliers   suppliers this order has been sent to (`getSentToSuppliers`, rendered as
 *                       "Previously Contacted Suppliers")
 *   hasRoundHandoff     this round carried a note or documents from Make it In Review, which only exists
 *                       after a quote has come back and been sent for revision
 *
 * Read `hasRoundHandoff` from what the hand-off arrived with, never from the note box's current value —
 * staff clearing the box is them deciding to send nothing, not the round becoming a first send.
 */
export function isRepeatSupplierRound({ previousSuppliers = [], hasRoundHandoff = false } = {}) {
  // Deliberately an order-level question. See the module comment: the recipient is always new to the order,
  // so there is no per-supplier version of this to ask.
  return previousSuppliers.length > 0 || Boolean(hasRoundHandoff);
}

/**
 * Label, placeholder and helper for the "Notes to supplier" box on Send to Supplier.
 *
 * The helper text does not change: what it promises — only this text is sent, blank sends nothing — is the
 * guarantee PGSS-5332 put in place, and it holds on every round.
 *
 * Pass `null` while the round is still unknown. The Make it In Review hand-off is fetched when the dialog
 * opens, so for one render the screen genuinely cannot say which round this is — and guessing prints the
 * wrong prompt and then swaps it out under staff, which is the very thing this module exists to stop. The
 * label and helper still render; only the prompt waits, and a prompt is decoration on an empty box.
 */
export function supplierNoteFieldCopy(isRepeatRound) {
  return {
    label: 'Notes to supplier (optional)',
    placeholder: roundPlaceholder(isRepeatRound),
    helperText: 'Only what you write here is sent. Leave blank to send no note.',
  };
}

function roundPlaceholder(isRepeatRound) {
  if (isRepeatRound === null || isRepeatRound === undefined) {
    return '';
  }

  return isRepeatRound
    ? 'Why is this order going out for quote again? This is shown to the supplier.'
    : 'Anything the supplier should know about this request? This is shown to the supplier.';
}

/**
 * Headings for the panel the supplier reads on Submit Quote. Named for the channel, not the round — see the
 * module comment. The second is the document-only case, where staff sent files and no note.
 */
export const SUPPLIER_NOTE_HEADING = 'Note to supplier:';
export const SUPPLIER_DOCUMENTS_HEADING = 'Documents to supplier:';
